/* This Service Handles Overall Authetication & Authorization */
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  from,
  timer,
  EMPTY,
  throwError,
  firstValueFrom,
  filter,
  map,
  catchError,
  defer,
  mergeMap,
  finalize,
  of,
  tap,
  switchMap,
  distinctUntilChanged,
} from 'rxjs';
import { UserPayload, UserProfile, UserProfileWithOTP } from '@app/core/models/user.model';
import { LoginCredentials, SessionData } from '../models/auth.model';
import { UserService } from '@app/core/services/user.service';
import { ApiService } from '@app/core/services/api.service';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { isPlatformBrowser } from '@angular/common';
import { FirebaseAuthService } from './firebase-auth.service';
import { API_ENDPOINTS } from '@app/core/constants/api-endpoints.constants';
import {
  SuccessResponse,
  ErrorResponse,
  Result,
  OtpPayload,
  OtpResponse,
  OtpDetails,
} from '@app/core/models/common.model';
import { FirebaseError } from '@angular/fire/app';
import { HttpHeaders } from '@angular/common/http';
import { SessionService } from './session.service';
import { IdTokenResult, User } from '@angular/fire/auth';
import { APP_ROUTES } from '@app/core/constants/routes.constants';
import { ErrorHandlingService } from '@app/core/services/error-handling.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private platformId = inject(PLATFORM_ID);

  // private fireAuth = inject(Auth);
  // Reactive state using signals
  // public isLoading = signal<boolean>(false);
  // public isAuthenticated = signal<boolean>(false);
  // public currentFbUser = signal<FirebaseUser | null>(null);
  // public sessionData = signal<SessionData | null>(null);
  public currentUserProfile = signal<UserProfile | null>(null);

  // Observables for reactive programming

  /* isAuthenticated defines, if user is authenticated or not */
  private isAuthenticatedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isAuthenticated$: Observable<boolean> = this.isAuthenticatedSubject.asObservable();

  /* Loading to take care of firebase delays */
  // private isLoadingSubject = new BehaviorSubject<boolean>(true);
  // public isLoading$ = this.isLoadingSubject.asObservable();

  // private currentFbUserSubject = new BehaviorSubject<FirebaseUser | null>(null);
  // public currentFbUser$ = this.currentFbUserSubject.asObservable();

  private userProfileSubject = new BehaviorSubject<UserProfile | null | undefined>(undefined);
  public userProfile$ = this.userProfileSubject.asObservable();

  private sessionSubject = new BehaviorSubject<SessionData | null>(null);
  public session$ = this.sessionSubject.asObservable();

  // private currentTokenSubject = new BehaviorSubject<string | null>(null);
  // public currentToken$ = this.currentTokenSubject.asObservable();

  //TODO: move to constants
  private sessionStorageKey = 'icxbee_session';

  constructor(
    private router: Router,
    private fbAuth: FirebaseAuthService,
    private apiService: ApiService,
    private userService: UserService,
    private errorHandlingService: ErrorHandlingService
  ) {
    this.restoreSession();
    this.bindAuthHydrationAndProfile();
  }

  private bindAuthHydrationAndProfile() {
    this.fbAuth.ready$
      .pipe(
        // after Firebase is hydrated, listen to user changes
        switchMap(() => this.fbAuth.currentFbUser$),
        // only react when uid/null actually changes
        distinctUntilChanged((a, b) => (a?.uid ?? null) === (b?.uid ?? null)),
        switchMap((fbUser) => {
          if (!fbUser) {
            // resolved: logged out
            this.userProfileSubject.next(null);
            this.isAuthenticatedSubject.next(false);
            this.clearSession();
            return of<Result<UserProfile>>({
              success: false,
              errorCode: 'NO_USER',
              message: 'User is logged out',
            });
          }

          // mark loading while we fetch token+profile
          this.userProfileSubject.next(undefined);

          // session first (fresh token), then profile
          return from(fbUser.getIdTokenResult(true)).pipe(
            tap(async (tokenResult) => {
              const sessionData = await this.createSessionData(fbUser, tokenResult);
              if (tokenResult.token) this.setSession(sessionData);
            }),
            switchMap(() => this.userService.getUserProfileByUid(fbUser.uid)),
            tap((result) => {
              if (result.success) {
                this.userProfileSubject.next(result.data);
                this.isAuthenticatedSubject.next(true);
              } else {
                this.userProfileSubject.next(null);
                this.isAuthenticatedSubject.next(false);
              }
            }),
            catchError((err) =>
              of<Result<UserProfile>>({
                success: false,
                errorCode: 'BACKEND_ERROR',
                message: err?.message || 'Failed to fetch profile',
              })
            )
          );
        })
      )
      .subscribe((result) => {
        if (result.success) {
          console.log('✅ Loaded profile & session:', result.data);
        } else {
          console.warn(`⚠️ Profile/session setup failed [${result.errorCode}]: ${result.message}`);
        }
      });
  }

  //TODO: PROPER ERROR HANDLING ACROSS
  //TODO: All methods to return Result observables rather than plain domain

  /* Refactored Code Starts */

  // Done Refactoring: 11/20/2025
  register(data: UserPayload): Observable<Result<UserProfileWithOTP>> {
    this.clearSession();
    return defer(() =>
      from(this.fbAuth.signUpWithEmail(data.email, data.password)).pipe(
        mergeMap((credential) =>
          //  Convert token Promise -> Observable
          from(credential.user.getIdToken()).pipe(
            mergeMap((token) => {
              // Add identifiers to payload
              data.profileIdentifiers = [
                {
                  uid: credential.user.uid,
                  emailId: credential.user.email!,
                  providerId: credential.user.providerId,
                  registrationMethod: 'self',
                },
              ];
              // Call backend with token
              return this.createUser(data, token).pipe(
                // handle Result<UserProfileWithOTP> here
                mergeMap((result) => {
                  return of(result);
                }),
                catchError((error) => {
                  this.fbAuth.rollbackFirebaseUser();
                  return throwError(() =>
                    this.apiService.handleApiError<UserProfileWithOTP>(error)
                  );
                })
              );
            })
          )
        ),
        catchError((error) =>
          throwError(() => {
            return new Error(this.fbAuth.formatError(error));
          })
        )
      )
    );
  }

  // Done Refactoring: 11/20/2025
  createUser(payload: UserPayload, token: string): Observable<Result<UserProfileWithOTP>> {
    return this.apiService
      .post<SuccessResponse<UserProfileWithOTP>, UserPayload>(
        API_ENDPOINTS.AUTH.REGISTER,
        payload,
        {
          headers: new HttpHeaders({
            Authorization: `Bearer ${token}`,
          }),
        }
      )
      .pipe(
        map((res) => ({ success: true as const, data: res.data })),
        catchError((error) => {
          return throwError(() => this.apiService.handleApiError<UserProfileWithOTP>(error));
        })
      );
  }

  // Done Refactoring: 11/20/2025
  private otpDataSubject = new BehaviorSubject<OtpDetails | null>(null);
  public otpData$ = this.otpDataSubject.asObservable();

  setOtpData(otpDetails: OtpDetails): void {
    this.otpDataSubject.next(otpDetails);
  }

  getOtpData(): OtpDetails | null | undefined {
    return this.otpDataSubject.value;
  }
  clearOtpData(): void {
    this.otpDataSubject.next(null);
  }

  private resetPasswordUidSubject = new BehaviorSubject<string | null>(null);
  public resetPasswordUid$ = this.resetPasswordUidSubject.asObservable();

  setResetPasswordUid(uid: string): void {
    this.resetPasswordUidSubject.next(uid);
  }

  clearResetPasswordUid(): void {
    this.resetPasswordUidSubject.next(null);
  }

  getResetPasswordUid(): string | null | undefined {
    return this.resetPasswordUidSubject.value;
  }

  

  loginWithEmail(data: LoginCredentials): Observable<Result<UserProfile>> {
    return from(this.fbAuth.loginWithEmail(data.email, data.password, data.rememberMe!)).pipe(
      mergeMap((loginResult) => {
        // 1️⃣ Firebase login failed
        if (!loginResult.success) {
          return throwError(() => new Error(this.fbAuth.formatError(loginResult)));
        }
        // 2️⃣ Firebase login successful → call backend to fetch profile
        return this.userService.getUserProfileByUid(loginResult.data.uid).pipe(
          tap((result) => {
            if (result.success) {
              // ✅ Profile fetched successfully → set state
              this.userProfileSubject.next(result.data);
              this.currentUserProfile.set(result.data);
              this.isAuthenticatedSubject.next(true);
            } else {
              // ❌ Profile fetch failed (including EMAIL_NOT_VERIFIED)
              this.userProfileSubject.next(null);
              this.currentUserProfile.set(null);
              this.isAuthenticatedSubject.next(false);
            }
          }),

          map((result): Result<UserProfile> => {
            // handle backend error code semantics
            // if (!result.success && result.error?.code === 'EMAIL_NOT_VERIFIED') {
            //   return {
            //     success: false,
            //     message: result.error.message || 'Your email has not been verified.',
            //     errorCode: 'EMAIL_NOT_VERIFIED',
            //     error: result.error,
            //   };
            // }

            // if (!result.success) {
            //   return {
            //     success: false,
            //     message: result.error?.message || result.message  || 'Profile fetch failed.',
            //     errorCode: result.error?.code || result.errorCode || 'PROFILE_FETCH_FAILED',
            //     error: result.error,
            //     errors: result.errors,
            //   };
            // }

            // ✅ Success
            return result;
          }),

          catchError((error) =>
            throwError(() => this.apiService.handleApiError<UserProfile>(error))
          )
        );
      }),

      // 5️⃣ Handle Firebase-level errors
      catchError((error) =>
        throwError(() => {
          return new Error(this.fbAuth.formatError(error));
        })
      )
    );
  }

  setUserProfile(profile: UserProfile) {
    console.log('user Profile received. inside auth service.', profile);
    this.bindAuthHydrationAndProfile();
    //  this.userProfileSubject.next(profile);
    //  this.currentUserProfile.set(profile);
    //  this.isAuthenticatedSubject.next(true);
  }

  // // Session Management

  private async createSessionData(user: User, tokenResult: IdTokenResult): Promise<SessionData> {
    return {
      uid: user.uid,
      email: user.email!,
      token: tokenResult.token,
      // refreshToken: user.refreshToken,
      // expirationTime: new Date(tokenResult.expirationTime).getTime(),
      lastActivity: Date.now(),
      createdAt: Date.now(),
    };
  }

  private setSession(sessionData: SessionData): void {
    // this.sessionData.set(sessionData);
    this.sessionSubject.next(sessionData);

    // Persist to storage
    if (isPlatformBrowser(this.platformId)) {
      const storage = environment.auth.persistenceType === 'local' ? localStorage : sessionStorage;
      storage.setItem(this.sessionStorageKey, JSON.stringify(sessionData));
    }
  }

  private async clearSession(): Promise<void> {
    // this.currentFbUser.set(null);
    // this.currentUserProfile.set(null);
    // this.sessionData.set(null);
    // this.currentFbUserSubject.next(null);

    // Clear storage
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(this.sessionStorageKey);
      sessionStorage.removeItem(this.sessionStorageKey);
    }
    this.userProfileSubject.next(null);
    this.isAuthenticatedSubject.next(false);
    this.sessionSubject.next(null);
    // Clear token refresh timer
    // if (this.tokenRefreshTimer) {
    //   clearTimeout(this.tokenRefreshTimer);
    //   this.tokenRefreshTimer = undefined;
    // }
  }

  private restoreSession(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const storage = environment.auth.persistenceType === 'local' ? localStorage : sessionStorage;
    const storedSession = storage.getItem(this.sessionStorageKey);

    if (storedSession) {
      try {
        const sessionData: SessionData = JSON.parse(storedSession);

        // Check if session is still valid
        if (this.isSessionValid(sessionData)) {
          //   this.sessionData.set(sessionData);
          this.sessionSubject.next(sessionData);

          // Session will be validated when Firebase auth state changes
        } else {
          // Clear expired session
          storage.removeItem(this.sessionStorageKey);
        }
      } catch (error) {
        console.error('Error parsing stored session:', error);
        storage.removeItem(this.sessionStorageKey);
      }
    }
  }

  private isSessionValid(sessionData: SessionData): boolean {
    const now = Date.now();
    const sessionAge = now - sessionData.createdAt;
    // const timeSinceLastActivity = now - sessionData.lastActivity;
    // const maxAge = 15 * 24 * 60 * 60 * 1000; // 15 days

    // Check if session hasn't exceeded maximum age
    // if (sessionAge > environment.auth.sessionTimeout) {
    //   return false;
    // }

    return sessionAge <= environment.auth.sessionTimeout;
  }

  async logout(): Promise<void> {
    try {
      await this.fbAuth.logout();
      this.clearSession();
      this.router.navigate([APP_ROUTES.AUTH.ROOT, APP_ROUTES.AUTH.SIGNIN]);
    } catch (error) {
      console.error('Error during logout:', error);
      throw error; // let component decide
    }
  }

  // Public getters for templates
  get authState(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  getToken() {
    return this.fbAuth.getFreshToken();
  }

  getUid() {
    return this.fbAuth.getUid();
  }

  getUserProfile(): UserProfile | null | undefined {
    return this.userProfileSubject.value;
  }

  clearUserProfile(): void {
    this.currentUserProfile.set(null);
    this.userProfileSubject.next(null);
  }

  async refreshUserProfile(): Promise<void> {
    this.bindAuthHydrationAndProfile();
  }
}
